import { firstQueryValue } from "~/utils/firstQueryValue";

/**
 * Reads an email out of a route query value.
 *
 * vue-router's `parseQuery` pre-decodes `+` as a space (form-encoding
 * compatibility — `searchParam.replace(PLUS_RE, " ")`), so an invite link carrying
 * `?email=max+board@anddine.co.uk` arrives as `max board@anddine.co.uk`. That breaks
 * the org-invite prefill: the pending org-customer lookup 404s on the mangled
 * address, its `catch` is deliberately silent, so the company name and organisation
 * delivery address just render empty — and the customer registers under an email
 * that doesn't match their invite. `decodeURI()` on top is a no-op and
 * `URLSearchParams` behaves identically; the restoration has to be explicit.
 *
 * A space is never legal in the addr-spec of an unquoted email, so putting the `+`
 * back is unambiguous. It also passes a correctly encoded `%2B` through untouched,
 * because vue-router has already turned that back into a literal `+` by this point.
 *
 * Originally written on `feature/org-invite-prefill-fix` (PR #199), which was
 * **closed unmerged** on 2026-09-28 — so this bug was live on `main` for three weeks
 * while CLAUDE.md and `docs/decisions.md` both described it as fixed. Brought in
 * here at @giokmn's request on PR #213.
 *
 * **Deviation from the original**, deliberate: that version returned `""` for an
 * array, which would now bounce a repeated `?email=&email=x` to `/sign-up`. Value
 * selection is `firstQueryValue`'s job and lives in exactly one place — see its
 * docblock for why a guard and its page must not each derive their own rule.
 *
 * Emails only. Do NOT use this for `firstName` / `lastName`: a name may legitimately
 * contain a space, and this would turn "Mary Jane" into "Mary+Jane".
 */
export function emailFromQuery(value: unknown): string {
  return firstQueryValue(value as Parameters<typeof firstQueryValue>[0])
    .trim()
    .replace(/ /g, "+")
    .toLowerCase();
}

export default emailFromQuery;
